import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '../env.js';
import { getCurrentGeo } from './geolocation';
import { isImpersonationTab } from './impersonation-bootstrap';

/**
 * Axios instance targeting the gateway. In dev, Vite proxies /api and /openapi
 * to http://localhost:3000. In prod, same-origin (frontend served behind gateway
 * or reverse-proxy) is assumed unless VITE_API_ORIGIN overrides it.
 */
export const apiClient = axios.create({
  baseURL: env.VITE_API_ORIGIN,
  // The refresh token lives in an HttpOnly cookie set by svc-auth, so the
  // rotation call must carry the cookie jar. The gateway echoes the origin and
  // sets `access-control-allow-credentials`, which is what makes this legal
  // cross-origin.
  withCredentials: true,
  timeout: 20_000,
});

// ---------------------------------------------------------------------------
// RFC 7807 problem-details error
// ---------------------------------------------------------------------------

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string;
  /**
   * Zod-style issue map, where each key is a dot-path into the request body
   * and the value is a list of human-readable error messages.
   */
  errors?: Record<string, string[]>;
  /**
   * RFC 7807 extension members (`AppError.toProblem()`), the machine-readable
   * half of a refusal. A screen that has to state a fact the refusal carries —
   * the moment a self-exclusion becomes liftable, say — reads it from here and
   * writes its own sentence around it, rather than parsing the English
   * `detail`.
   */
  extensions?: Record<string, unknown>;
}

export class ApiError extends Error {
  status: number;
  title: string;
  detail?: string;
  code?: string;
  errors?: Record<string, string[]>;
  extensions?: Record<string, unknown>;
  raw?: unknown;

  constructor(opts: {
    status: number;
    title: string;
    detail?: string;
    code?: string;
    errors?: Record<string, string[]>;
    extensions?: Record<string, unknown>;
    raw?: unknown;
  }) {
    super(opts.detail || opts.title);
    this.name = 'ApiError';
    this.status = opts.status;
    this.title = opts.title;
    this.detail = opts.detail;
    this.code = opts.code;
    this.errors = opts.errors;
    this.extensions = opts.extensions;
    this.raw = opts.raw;
  }

  /**
   * Best human-readable string. Prefer `detail`, fall back to `title` — but
   * never surface a `detail` that is a serialized JSON object/array or an S2S
   * `… returned <status>:` wrapper (some backend paths still emit those); those
   * would render as an unreadable blob in a toast.
   */
  get displayMessage(): string {
    const detail =
      this.detail && !/^\s*[[{]/.test(this.detail) && !/returned \d{3}/.test(this.detail)
        ? this.detail
        : undefined;
    return detail || this.title || 'Request failed';
  }
}

function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  const axErr = err as AxiosError<ProblemDetails>;
  if (axErr && axErr.isAxiosError) {
    const status = axErr.response?.status ?? 0;
    const body = axErr.response?.data;
    if (body && typeof body === 'object') {
      return new ApiError({
        status,
        title: body.title ?? axErr.message ?? 'Request failed',
        detail: body.detail,
        code: body.code,
        errors: body.errors,
        extensions: body.extensions,
        raw: body,
      });
    }
    // No problem-details body. status 0 = no response reached us (DNS, refused,
    // CORS, timeout). Surface the axios code + message so the cause is visible
    // instead of an opaque "Network unreachable".
    const netDetail =
      status === 0
        ? `${axErr.code ?? 'NETWORK_ERROR'}: ${axErr.message || 'Network unreachable'} (${apiClient.defaults.baseURL ?? 'same-origin'})`
        : undefined;
    return new ApiError({
      status,
      title: axErr.message || 'Network error',
      detail: netDetail,
      code: axErr.code,
      raw: axErr,
    });
  }
  return new ApiError({
    status: 0,
    title: (err as Error)?.message ?? 'Unknown error',
    raw: err,
  });
}

// ---------------------------------------------------------------------------
// Request interceptor — attach Bearer token from the auth store
// ---------------------------------------------------------------------------
const AUDIENCE_SEGMENT = /^\/api\/([^/]+)\//;
const SIGN_IN = /\/auth\/login(?:[?#]|$)/;

/**
 * Whether a request to `url` may carry the session's bearer. A sign-in never does, and neither
 * does a request to the other audience's gateway (staff on `/api/bo` and `/api/v1`, partners on
 * `/api/partner`): that gateway refuses a foreign bearer with 401 AUTH-004, even on a public route.
 */
function sendsSessionBearer(url: string | undefined, ownAudience: 'bo' | 'partner'): boolean {
  const path = url ?? '';
  if (SIGN_IN.test(path)) return false;
  const segment = AUDIENCE_SEGMENT.exec(path)?.[1];
  if (segment === undefined) return true;
  return (segment === 'v1' ? 'bo' : segment) === ownAudience;
}

async function ownsRequest(url: string | undefined): Promise<boolean> {
  try {
    const mod = await import('@/stores/auth-store');
    return sendsSessionBearer(url, mod.audienceFor(mod.useAuthStore.getState().subjectKind));
  } catch {
    return true;
  }
}

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  try {
    const mod = await import('@/stores/auth-store');
    const { accessToken: token, subjectKind } = mod.useAuthStore.getState();
    if (token && sendsSessionBearer(config.url, mod.audienceFor(subjectKind))) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
  } catch {
    /* first-load timing; proceed without token */
  }
  // Attach the latest cached geolocation reading. If the browser denied
  // permission or hasn't resolved yet, headers are simply omitted —
  // backend treats null GPS as a normal request.
  const geo = getCurrentGeo();
  if (geo) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)['x-geo-lat'] = String(geo.lat);
    (config.headers as Record<string, string>)['x-geo-lng'] = String(geo.lng);
  }
  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor — 401 refresh flow + ApiError normalization
// ---------------------------------------------------------------------------
let refreshInFlight: Promise<string | null> | null = null;

type RetryableConfig = AxiosRequestConfig & { _retry?: boolean };
const REFRESH_REQUEST_CONFIG: RetryableConfig = { _retry: true };

apiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;

    // 401 handling: attempt a single refresh + replay. If that fails, logout.
    if (original && status === 401 && !original._retry) {
      if ((original.url ?? '').includes('/auth/refresh')) {
        await triggerLogoutAndRedirect();
        return Promise.reject(toApiError(error));
      }
      if (!(await ownsRequest(original.url))) return Promise.reject(toApiError(error));
      original._retry = true;
      try {
        if (!refreshInFlight) {
          refreshInFlight = (async () => {
            const mod = await import('@/stores/auth-store');
            const state = mod.useAuthStore.getState();
            // An impersonation tab carries a short-lived, non-renewable token.
            // The refresh cookie is origin-wide, so refreshing here would swap
            // the impersonated session for the parent admin's own.
            if (isImpersonationTab()) return null;
            // Must go through apiClient, not bare axios: only the instance
            // carries baseURL (the gateway host) and withCredentials (the
            // refresh cookie). A bare relative POST lands on the static-hosting
            // origin, where the SPA rewrite answers 200 + index.html and the
            // absent accessToken reads as a failed refresh.
            const { data } = await apiClient.post<{
              accessToken: string;
              refreshToken?: string;
            }>(
              `/api/${mod.audienceFor(state.subjectKind)}/auth/refresh`,
              // Body token is legacy/native only; web rotates on the cookie.
              state.refreshToken ? { refreshToken: state.refreshToken } : {},
              // Pre-marked so a 401 on the rotation itself falls straight to
              // the catch below instead of re-entering this branch.
              REFRESH_REQUEST_CONFIG,
            );
            state.setTokens(data.accessToken, data.refreshToken ?? null);
            return data.accessToken;
          })().finally(() => {
            refreshInFlight = null;
          });
        }
        const newToken = await refreshInFlight;
        if (!newToken) {
          await triggerLogoutAndRedirect();
          return Promise.reject(toApiError(error));
        }
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
        return apiClient.request(original);
      } catch (refreshErr) {
        await triggerLogoutAndRedirect();
        return Promise.reject(toApiError(refreshErr));
      }
    }

    return Promise.reject(toApiError(error));
  },
);

async function triggerLogoutAndRedirect() {
  let loginPath = '/login';
  try {
    const mod = await import('@/stores/auth-store');
    const state = mod.useAuthStore.getState();
    if (state.subjectKind === 'partner') loginPath = '/partner/login';
    await state.logout();
  } catch {
    /* best-effort */
  }
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith(loginPath)) {
    window.location.assign(loginPath);
  }
}

// ---------------------------------------------------------------------------
// Typed helpers
// ---------------------------------------------------------------------------

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  [key: string]: unknown;
}

function unwrap<T>(p: Promise<{ data: T }>): Promise<T> {
  return p.then((r) => r.data);
}

export const api = {
  get<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
    return unwrap(apiClient.get<T>(path, config));
  },
  post<T, B = unknown>(path: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
    return unwrap(apiClient.post<T>(path, body, config));
  },
  put<T, B = unknown>(path: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
    return unwrap(apiClient.put<T>(path, body, config));
  },
  patch<T, B = unknown>(path: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
    return unwrap(apiClient.patch<T>(path, body, config));
  },
  delete<T = void>(path: string, config?: AxiosRequestConfig): Promise<T> {
    return unwrap(apiClient.delete<T>(path, config));
  },
  /**
   * GET a paginated list endpoint. Backend is expected to return
   * `{ items, total, page, pageSize }`. Query params are flattened into
   * the URL; undefined/null values are skipped.
   */
  listPaginated<T>(path: string, query: ListQuery = {}): Promise<PaginatedResponse<T>> {
    const params: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') params[k] = v;
    }
    return unwrap(apiClient.get<PaginatedResponse<T>>(path, { params }));
  },
};
